import type { ReactNode } from 'react'
import { Box, Typography } from '@mui/material'

/**
 * A group of fields with its title in a left rail. The title lines every group
 * up on one edge so the eye reads down the fields rather than across headings.
 * Below 768px the title stacks above its fields.
 *
 * A hairline separates groups; spacing comes from the wrapper so a group that
 * renders nothing leaves no gap.
 */
export default function FormSection({
  title,
  children,
  first,
}: {
  title: string
  children: ReactNode
  first?: boolean
}) {
  return (
    <Box
      component="section"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '168px minmax(0, 1fr)' },
        columnGap: 4,
        rowGap: 1.5,
        py: 2.75,
        borderTop: first ? 0 : 1,
        borderColor: 'divider',
        '&:first-of-type': { pt: 0, borderTop: 0 },
        '&:last-of-type': { pb: 0 },
      }}
    >
      <Typography variant="subtitle2" component="h3" sx={{ lineHeight: '1.25rem' }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>{children}</Box>
    </Box>
  )
}

/** Two fields side by side from `sm`, stacked on a phone. */
export function FieldPair({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
        gap: 2,
        alignItems: 'start',
      }}
    >
      {children}
    </Box>
  )
}
